<main class="portal-page">
  <div class="loading-view" *ngIf="loading" role="status" aria-live="polite">
    <span class="loading-spinner" aria-hidden="true"></span>
    <p>Checking your session…</p>
  </div>

  <section class="login-shell" *ngIf="!loading && !session" aria-labelledby="sign-in-title">
    <div class="login-frame">
      <img class="kasha-logo" src="assets/img/kasha_logo.png" alt="Kasha">
      <div class="login-card">
        <h1 id="sign-in-title">HIV Product Delivery Service</h1>
        <p class="login-intro">Sign in with your account to access HIV-PDS resources</p>
        <div id="google-signin" class="google-signin" aria-label="Sign in with Google"></div>
        <div class="login-divider"><span>or</span></div>
        <p class="auth-message auth-message--error" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>
        <button class="email-signin-button" type="button" (click)="openEmailLogin()">
          Sign in with Email
        </button>
      </div>
    </div>

    <p class="login-security">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l7 3v5c0 4.6-2.9 8.1-7 10-4.1-1.9-7-5.4-7-10V6l7-3z"></path><path d="M9.3 12l1.8 1.8 3.8-4"></path></svg>
      Secure central access for authorized HIV PDS users
    </p>

    <div
      class="modal-backdrop"
      [class.modal-backdrop--open]="emailModalOpen"
      (click)="closeEmailLogin()"
      [attr.aria-hidden]="!emailModalOpen">
      <div class="email-modal" role="dialog" aria-modal="true" aria-labelledby="email-modal-title" (click)="$event.stopPropagation()">
        <button class="modal-close" type="button" (click)="closeEmailLogin()" aria-label="Close email sign in">&times;</button>
        <h2 id="email-modal-title">Sign in</h2>
        <form [formGroup]="loginForm" (ngSubmit)="signIn()" novalidate>
          <label class="sr-only" for="email">Email address</label>
          <input
            id="email"
            type="email"
            formControlName="email"
            autocomplete="email"
            placeholder="Email"
            [attr.aria-invalid]="loginForm.controls.email.invalid && loginForm.controls.email.touched">

          <label class="sr-only" for="password">Password</label>
          <div class="password-field">
            <input
              id="password"
              [type]="passwordVisible ? 'text' : 'password'"
              formControlName="password"
              autocomplete="current-password"
              placeholder="Password"
              [attr.aria-invalid]="loginForm.controls.password.invalid && loginForm.controls.password.touched">
            <button
              type="button"
              (click)="passwordVisible = !passwordVisible"
              [attr.aria-label]="passwordVisible ? 'Hide password' : 'Show password'">
              {{ passwordVisible ? '🙈' : '👁️' }}
            </button>
          </div>

          <p class="auth-message auth-message--error" *ngIf="errorMessage" role="alert">{{ errorMessage }}</p>
          <button class="sign-in-button" type="submit" [disabled]="submitting">
            <span>{{ submitting ? 'Signing in…' : 'Sign In' }}</span>
          </button>
        </form>
      </div>
    </div>
  </section>

  <div class="portal-shell" *ngIf="!loading && session">
    <div class="session-bar">
      <div class="session-user">
        <span class="user-avatar" aria-hidden="true">{{ session.email.charAt(0).toUpperCase() }}</span>
        <span>Signed in as <strong>{{ session.email }}</strong></span>
      </div>
      <button type="button" (click)="logout()">Sign out</button>
    </div>

    <header class="portal-header">
      <img class="kasha-logo" src="assets/img/kasha_logo.png" alt="Kasha">
      <h1>HIV PDS Access</h1>
      <p>Select an authorized service area to continue</p>
    </header>

    <section
      *ngFor="let area of visibleAccessAreas; trackBy: trackArea"
      class="access-area"
      [ngClass]="'access-area--' + area.theme"
      [attr.aria-labelledby]="area.id + '-title'">
      <div class="area-header">
        <div class="area-heading">
          <span class="icon-badge icon-badge--large" aria-hidden="true">
            <ng-container *ngTemplateOutlet="icon; context: {$implicit: area.icon}"></ng-container>
          </span>
          <div>
            <h2 [id]="area.id + '-title'">{{ area.title }}</h2>
            <p>{{ area.description }}</p>
          </div>
        </div>

        <span class="area-access-note">Choose a service below</span>
      </div>

      <div class="service-grid">
        <button
          class="service-card"
          type="button"
          *ngFor="let service of visibleServices(area); trackBy: trackService"
          (click)="accessService(service)"
          [disabled]="openingService === service.title"
          [class.service-card--unavailable]="!serviceAvailable(service)"
          [attr.aria-label]="'Open ' + service.title">
          <span class="icon-badge" aria-hidden="true">
            <ng-container *ngTemplateOutlet="icon; context: {$implicit: service.icon}"></ng-container>
          </span>
          <div class="service-copy">
            <h3>{{ service.title }}</h3>
            <p>{{ service.description }}</p>
          </div>
          <span class="service-launch" aria-hidden="true">
            {{ openingService === service.title ? '…' : '→' }}
          </span>
        </button>
        <p class="no-services" *ngIf="visibleServices(area).length === 0">
          No services in this area are assigned to your current role.
        </p>
      </div>
    </section>

    <section class="no-access" *ngIf="visibleAccessAreas.length === 0" role="status">
      <span class="icon-badge icon-badge--large" aria-hidden="true">
        <ng-container *ngTemplateOutlet="icon; context: {$implicit: 'person'}"></ng-container>
      </span>
      <h2>No HIV PDS access assigned</h2>
      <p>Your login is valid, but your profile is not currently assigned to Kenya, South Africa, or Insights.</p>
      <p>Contact your administrator to request the appropriate role or organisation.</p>
    </section>

    <p class="auth-message auth-message--notice" *ngIf="errorMessage" role="status">{{ errorMessage }}</p>

  </div>
</main>

<ng-template #icon let-name>
  <svg *ngIf="name === 'globe'" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"></circle><path d="M3 12h18M12 3c3 3 4 6 4 9s-1 6-4 9M12 3c-3 3-4 6-4 9s1 6 4 9"></path></svg>
  <svg *ngIf="name === 'person'" viewBox="0 0 24 24"><circle cx="12" cy="7" r="3.5"></circle><path d="M5.5 21v-2.2a6.5 6.5 0 0113 0V21"></path></svg>
  <svg *ngIf="name === 'truck'" viewBox="0 0 24 24"><path d="M3 5h11v11H3zM14 9h4l3 4v3h-7z"></path><circle cx="7" cy="18" r="2"></circle><circle cx="18" cy="18" r="2"></circle></svg>
  <svg *ngIf="name === 'chart'" viewBox="0 0 24 24"><path d="M5 20v-5M10 20V9M15 20V5M20 20V2"></path></svg>
  <svg *ngIf="name === 'monitor'" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="13" rx="1.5"></rect><path d="M8 21h8M12 17v4M6 13l4-4 3 2 5-5"></path></svg>
  <svg *ngIf="name === 'brain'" viewBox="0 0 24 24"><path d="M9 4a3 3 0 00-5 2 3 3 0 00-1 5 3 3 0 002 5 3 3 0 004 3M15 4a3 3 0 015 2 3 3 0 011 5 3 3 0 01-2 5 3 3 0 01-4 3M9 4v16M15 4v16M9 8H7M15 8h2M9 13H6M15 13h3"></path></svg>
  <svg *ngIf="name === 'trend'" viewBox="0 0 24 24"><path d="M3 20h18M5 17l5-6 4 3 6-9M15 5h5v5"></path></svg>
</ng-template>
